/**
 * Criteria step of the cycle configure wizard.
 *
 * A cycle has no criteria of its own: criteria are each track's Criteria & Scales
 * rows (`cycle_attribute_configs`, track-scoped — canon 28 §5.1). This step writes
 * those rows on the tracks it lists under "Writes to", through the calls the track
 * page's Criteria & Scales tab makes: `createCycleConfig` to add a criterion,
 * `deleteCycleConfig` to take one off. A track whose criteria are locked, or that
 * the person may not change (migration 1006), is listed with the reason and left
 * alone.
 *
 * Weights are the next step (CycleWeightsPanel): each criterion's weight is saved
 * on the same rows (migration 1047).
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Check, AlertCircle, Target, Loader2 } from 'lucide-react';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import {
  useAttributeGovernanceStore,
  criteriaRowsForTrack,
  type CycleAttributeConfig,
} from '../../../stores/prioritization/attributeGovernanceStore';
import { isCriterionEligible } from '../../../lib/scoringDimensions';
import { useCriteriaStore } from '../../../stores/prioritization/criteriaStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useCriterionReading, useCriterionIsWorkspaceOwn } from '../../../hooks/prioritization/useCriterionReading';
import { useCycleTracksAuthority } from '../../../hooks/prioritization/useTrackAuthority';
import {
  CRITERION_READING_PARTS,
  criterionIncompleteWarning,
  criterionReadingOf,
  type CriterionReading,
  type CriterionReadingField,
} from '../../../lib/criterionReading';
import { keyCoverage, planTrackWrites, wizardTrackTarget } from '../../../lib/cycleWizardTargets';
import { Field, TextInput } from '@/components/ds';
import {
  CycleTrackTargets,
  TrackWriteReport,
  type TrackWriteFailure,
} from './CycleTrackTargets';
import { useChosenTracks, useCycleOpenTracks } from '../../../hooks/prioritization/useCycleOpenTracks';
import { useCycleObjectives } from '../../../hooks/prioritization/useCycleObjectives';
import { isObjectiveCriterionKey, withoutObjectiveAttributes } from '../../../lib/objectiveCriteria';
import { ObjectiveCriteriaChooser } from './ObjectiveCriteriaChooser';

// ============================================================================
// Types
// ============================================================================

export interface CriteriaConfigPanelProps {
  /** The cycle whose tracks this step writes to */
  cycleId: string;

  /** Criteria the method picked in the first step scores on: ticked, and kept ticked */
  requiredCriteriaIds?: string[];

  /** Name of that method, for display */
  selectedMethodName?: string;
}

// ============================================================================
// What a criterion measures: for whom, against what, and its scale
// ============================================================================

/**
 * The three things a criterion names so a value on it reads later (owner ruling
 * 2026-10-28; migration 967). Each saves when the person leaves the field. A
 * criterion missing any of them gets a plain warning; it is never blocked.
 *
 * The fields are drawn only when the save would be allowed (build contract
 * §2.8): the workspace's first word creates its own criteria row, which the
 * database allows only to a holder of criteria.create (migration 1000); later
 * words update that row (criteria.edit).
 */
function CriterionReadingFields({
  attribute,
  mayEdit,
  mayCreate,
}: {
  attribute: { key: string; label: string };
  /** The person may edit criteria (criteria.edit). */
  mayEdit: boolean;
  /** The person may create criteria (criteria.create). */
  mayCreate: boolean;
}) {
  const reading = useCriterionReading(attribute.key);
  const own = useCriterionIsWorkspaceOwn(attribute.key);
  const editable = mayEdit && (own || mayCreate);
  const needsCreateRight = mayEdit && !own && !mayCreate;
  const saveCriterionReading = useCriteriaStore((s) => s.saveCriterionReading);
  const [draft, setDraft] = useState<Partial<Record<CriterionReadingField, string>>>({});
  const [error, setError] = useState<string | null>(null);

  // The warning reads what the person sees: what is saved, with what they have typed.
  const shown: CriterionReading = criterionReadingOf({ ...reading, ...draft });
  const warning = criterionIncompleteWarning(shown);

  const commit = async (field: CriterionReadingField) => {
    const typed = draft[field];
    if (typed === undefined) return;
    const next = criterionReadingOf({ [field]: typed })[field];
    if (next === reading[field]) {
      setDraft(({ [field]: _done, ...rest }) => rest);
      return;
    }
    const result = await saveCriterionReading(attribute, { [field]: next });
    if (result.error) {
      setError(`Not saved: ${result.error}`);
      return;
    }
    setError(null);
    setDraft(({ [field]: _done, ...rest }) => rest);
  };

  return (
    <div
      className="mt-3 cursor-auto space-y-3 border-t border-[var(--rule)] pt-3"
      onClick={(e) => e.stopPropagation()}
      data-testid={`criterion-reading-fields-${attribute.key}`}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        {CRITERION_READING_PARTS.map((part) =>
          editable ? (
            <Field key={part.field} label={part.label} hint={part.hint}>
              <TextInput
                size="sm"
                value={draft[part.field] ?? reading[part.field] ?? ''}
                placeholder={part.placeholder}
                onChange={(e) => setDraft((d) => ({ ...d, [part.field]: e.target.value }))}
                onBlur={() => void commit(part.field)}
              />
            </Field>
          ) : (
            <div key={part.field} className="flex flex-col gap-1">
              <span className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
                {part.label}
              </span>
              <span className="text-sm text-[var(--text-sec)]">{reading[part.field] ?? 'Not said'}</span>
            </div>
          ),
        )}
      </div>
      {needsCreateRight && (
        <p className="text-xs text-[var(--text-ter)]" role="status">
          Saying this for the first time creates the criterion in this workspace, which needs the right to create criteria.
        </p>
      )}
      {warning && (
        <p className="flex items-start gap-1.5 text-xs text-[var(--mustard)]" role="status">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {warning}
        </p>
      )}
      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function CriteriaConfigPanel({
  cycleId,
  requiredCriteriaIds = [],
  selectedMethodName,
}: CriteriaConfigPanelProps) {
  // Saying what a criterion measures edits the criterion definition (criteria.edit);
  // the workspace's first word creates its criterion row (criteria.create, migration 1000).
  const can = useUserStore(state => state.can);
  const mayEditCriteria = can('criteria.edit');
  const mayCreateCriteria = can('criteria.create');

  const attributes = useAttributesStore(state => state.attributes);
  const fetchAttributes = useAttributesStore(state => state.fetchFromSupabase);
  const availableCriteria = useMemo(
    // System (non-custom) attributes are always criterion-eligible; custom
    // fields only when opted in via usable_as_criterion.
    // Strategic objectives are ticked in their own list (1066), not among the attributes.
    () => withoutObjectiveAttributes(attributes).filter(isCriterionEligible),
    [attributes],
  );

  const cycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs);
  const fetchCycleConfigs = useAttributeGovernanceStore(state => state.fetchCycleConfigs);
  const createCycleConfig = useAttributeGovernanceStore(state => state.createCycleConfig);
  const deleteCycleConfig = useAttributeGovernanceStore(state => state.deleteCycleConfig);

  useEffect(() => {
    void fetchAttributes();
    void fetchCycleConfigs(cycleId);
  }, [cycleId, fetchAttributes, fetchCycleConfigs]);

  // Which tracks this step writes to: open, criteria not locked, and the person may change them.
  const { tracks } = useCycleOpenTracks(cycleId);
  const authorityOf = useCycleTracksAuthority(cycleId);
  const targets = tracks.map(t => wizardTrackTarget('criteria', t, authorityOf(t).criteriaGate));
  const { chosenIds, toggle: toggleTrack } = useChosenTracks(targets);
  const mayWrite = chosenIds.length > 0;

  // What each track holds now: its Criteria & Scales rows.
  const rowsByTrack = useMemo(() => {
    const map = new Map<string, CycleAttributeConfig[]>();
    for (const t of tracks) map.set(t.id, criteriaRowsForTrack(cycleConfigs, t));
    return map;
  }, [tracks, cycleConfigs]);
  const present = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const [trackId, rows] of rowsByTrack) map.set(trackId, new Set(rows.map(r => r.attributeKey)));
    return map;
  }, [rowsByTrack]);

  // The method's criteria that this workspace has as attributes; the rest cannot be added.
  const requiredKeys = useMemo(
    () => requiredCriteriaIds.filter(key => availableCriteria.some(a => a.key === key)),
    [requiredCriteriaIds, availableCriteria],
  );
  const missingRequired = requiredCriteriaIds.filter(key => !requiredKeys.includes(key));

  // What the person has decided, per criterion; the method's criteria are always on.
  const [decided, setDecided] = useState<Map<string, boolean>>(new Map());
  const effectiveDecided = useMemo(() => {
    const map = new Map(decided);
    for (const key of requiredKeys) map.set(key, true);
    return map;
  }, [decided, requiredKeys]);

  const isOn = (key: string) => effectiveDecided.get(key) ?? keyCoverage(present, chosenIds, key) === 'all';

  const handleToggleCriterion = (key: string) => {
    if (!mayWrite || requiredKeys.includes(key)) return;
    const next = new Map(decided);
    next.set(key, !isOn(key));
    setDecided(next);
  };

  // Strategic objectives (1066): the cycle chooses which are in play; the plan adds and
  // removes them on the chosen tracks with the rest.
  const objectiveKeysPresent = useMemo(
    () => [...new Set([...present.values()].flatMap(set => [...set].filter(isObjectiveCriterionKey)))],
    [present],
  );
  const cycleObjectives = useCycleObjectives(cycleId, objectiveKeysPresent);

  const plan = planTrackWrites(present, chosenIds, effectiveDecided);
  const changeCount = plan.add.length + plan.remove.length;

  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<{ saved: number; failures: TrackWriteFailure[] } | null>(null);

  const trackName = (id: string) => tracks.find(t => t.id === id)?.name ?? 'A track';
  const labelOf = useCallback(
    (key: string) => attributes.find(a => a.key === key)?.label
      ?? cycleObjectives.options.find(o => o.key === key)?.objective.name
      ?? key,
    [attributes, cycleObjectives.options],
  );

  const handleSave = async () => {
    setSaving(true);
    const failures: TrackWriteFailure[] = [];
    let saved = 0;
    try {
      let addedObjective = false;
      for (const { trackId, key } of plan.add) {
        try {
          // An objective's scale is its own, so its range stands accepted (1066).
          const isObjective = isObjectiveCriterionKey(key);
          await createCycleConfig({ cycleId, trackId, attributeKey: key, rangeStatus: isObjective ? 'accepted' : 'pending' });
          if (isObjective) addedObjective = true;
          saved += 1;
        } catch (err) {
          failures.push({ trackName: trackName(trackId), what: `adding ${labelOf(key)}`, error: err instanceof Error ? err.message : String(err) });
        }
      }
      for (const { trackId, key } of plan.remove) {
        const row = rowsByTrack.get(trackId)?.find(r => r.attributeKey === key);
        if (!row) continue;
        const { error } = await deleteCycleConfig(row.id);
        if (error) failures.push({ trackName: trackName(trackId), what: `removing ${labelOf(key)}`, error });
        else saved += 1;
      }
      if (addedObjective) await fetchAttributes();
      await fetchCycleConfigs(cycleId);
      setDecided(new Map());
      setReport({ saved, failures });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)] flex items-center gap-2">
          <Target className="h-5 w-5 text-[var(--text-ter)]" />
          Criteria
        </h3>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          Pick the criteria these tracks score items on. Saving adds a ticked criterion to each
          track below that lacks it and takes an unticked one off — what Add Criteria and Remove
          do on a track&apos;s Criteria tab.
        </p>
      </div>

      <CycleTrackTargets targets={targets} chosenIds={chosenIds} onToggle={toggleTrack} />

      {selectedMethodName && requiredCriteriaIds.length > 0 && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
          <div className="flex items-start gap-2">
            <Target className="h-5 w-5 text-[var(--text-ter)] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[var(--text-sec)]">Scored by {selectedMethodName}</p>
              {requiredKeys.length > 0 && (
                <p className="text-xs text-[var(--text-sec)] mt-0.5">
                  {requiredKeys.map(labelOf).join(', ')} {requiredKeys.length === 1 ? 'is' : 'are'} ticked for it.
                </p>
              )}
              {missingRequired.length > 0 && (
                <p className="text-xs text-[var(--mustard)] mt-0.5">
                  It also scores on {missingRequired.join(', ')}, which this workspace has no attribute for.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {availableCriteria.length === 0 ? (
          <div className="p-4 bg-[var(--surface)] text-center text-[var(--text-ter)]">
            No criteria available. Create attributes first.
          </div>
        ) : (
          availableCriteria.map(criterion => {
            const on = isOn(criterion.key);
            const isRequired = requiredKeys.includes(criterion.key);
            const coverage = keyCoverage(present, chosenIds, criterion.key);
            const onCount = chosenIds.filter(id => present.get(id)?.has(criterion.key)).length;
            const toggleable = mayWrite && !isRequired;

            return (
              <div
                key={criterion.key}
                className={`p-4 border transition-colors ${
                  on ? 'bg-[var(--slate-dim)] border-[var(--slate)]' : 'bg-[var(--bg-raised)] border-[var(--rule)]'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    aria-label={criterion.label}
                    disabled={!toggleable}
                    onClick={() => handleToggleCriterion(criterion.key)}
                    className={`flex items-center gap-3 text-left ${toggleable ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      on ? 'bg-[var(--slate)] border-[var(--slate)]' : 'border-[var(--rule)]'
                    }`}>
                      {on && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span>
                      <span className="flex items-center gap-2">
                        <span className="font-medium text-[var(--text-pri)]">{criterion.label}</span>
                        {isRequired && (
                          <span className="px-1.5 py-0.5 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
                            {selectedMethodName ? `For ${selectedMethodName}` : 'Required'}
                          </span>
                        )}
                      </span>
                      {(criterion.definition || criterion.description) && (
                        <span className="block text-sm text-[var(--text-ter)]">{criterion.definition ?? criterion.description}</span>
                      )}
                    </span>
                  </button>
                  {coverage === 'some' && (
                    <span className="text-xs text-[var(--text-ter)]">
                      On {onCount} of {chosenIds.length} tracks
                    </span>
                  )}
                </div>
                {on && (
                  <CriterionReadingFields
                    attribute={{ key: criterion.key, label: criterion.label }}
                    mayEdit={mayEditCriteria}
                    mayCreate={mayCreateCriteria}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {cycleObjectives.strategyOn && (
        <div className="p-4 border border-[var(--rule)] bg-[var(--bg-raised)]">
          <ObjectiveCriteriaChooser
            options={cycleObjectives.options}
            loading={cycleObjectives.loading}
            strategyName={cycleObjectives.strategyName}
            isOn={isOn}
            onToggle={(o) => handleToggleCriterion(o.key)}
            disabled={!mayWrite}
            disabledReason={!mayWrite ? 'Choose a track above to add objectives to.' : undefined}
          />
        </div>
      )}

      {report && <TrackWriteReport saved={report.saved} failures={report.failures} />}

      {mayWrite && changeCount > 0 && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--rule)]">
          <span className="text-sm text-[var(--text-sec)]">
            {[plan.add.length > 0 ? `Adds ${plan.add.length}` : null, plan.remove.length > 0 ? `removes ${plan.remove.length}` : null]
              .filter(Boolean)
              .join(', ')}
          </span>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save criteria to {chosenIds.length} track{chosenIds.length === 1 ? '' : 's'}
          </button>
        </div>
      )}
    </div>
  );
}

export default CriteriaConfigPanel;
